import { useEffect, useState, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { getToken } from 'shared/config/store/reducers/userSlice';
import { useLazyGetUserQuery } from 'entities/user/api';
import { useLogoutMutation } from 'entities/user/api';
import { ROUTES } from 'shared/const/routes';

interface UseSessionTimeoutOptions {
  warningTime?: number;
  checkInterval?: number;
  inactivityTimeout?: number;
}

const DEFAULT_WARNING_TIME = 60;
const DEFAULT_CHECK_INTERVAL = 1000;
const DEFAULT_INACTIVITY_TIMEOUT = 15 * 60 * 1000;

export const useSessionTimeout = (options: UseSessionTimeoutOptions = {}) => {
  const {
    warningTime = DEFAULT_WARNING_TIME,
    checkInterval = DEFAULT_CHECK_INTERVAL,
    inactivityTimeout = DEFAULT_INACTIVITY_TIMEOUT,
  } = options;

  const token = useSelector(getToken);
  const [showModal, setShowModal] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(warningTime);
  const [extendSession] = useLazyGetUserQuery();
  const [logout] = useLogoutMutation();
  const lastActivityRef = useRef<number>(Date.now());
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const warningIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isExtendingRef = useRef(false);
  const logoutRef = useRef<() => Promise<void>>();

  const updateActivity = useCallback(() => {
    const now = Date.now();
    const wasInactive = now - lastActivityRef.current > inactivityTimeout - warningTime * 1000;
    lastActivityRef.current = now;
    
    if (showModal && wasInactive && !isExtendingRef.current) {
      setShowModal(false);
      setSecondsRemaining(warningTime);
      if (warningIntervalRef.current) {
        clearInterval(warningIntervalRef.current);
        warningIntervalRef.current = null;
      }
    }
  }, [showModal, inactivityTimeout, warningTime]);

  // Проверяем активность пользователя
  useEffect(() => {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    events.forEach((event) => {
      document.addEventListener(event, updateActivity, true);
    });

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, updateActivity, true);
      });
    };
  }, [updateActivity]);

  const handleLogout = useCallback(async () => {
    setShowModal(false);
    if (warningIntervalRef.current) {
      clearInterval(warningIntervalRef.current);
      warningIntervalRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    try {
      await logout().unwrap();
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      window.location.href = ROUTES.LOGIN.path;
    }
  }, [logout]);

  logoutRef.current = handleLogout;

  useEffect(() => {
    if (!token) {
      setShowModal(false);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (warningIntervalRef.current) {
        clearInterval(warningIntervalRef.current);
        warningIntervalRef.current = null;
      }
      return;
    }

    lastActivityRef.current = Date.now();

    const checkSession = () => {
      const now = Date.now();
      const timeSinceActivity = now - lastActivityRef.current;
      const warningThreshold = inactivityTimeout - warningTime * 1000;

      if (timeSinceActivity >= warningThreshold && timeSinceActivity < inactivityTimeout) {
        const remaining = Math.ceil((inactivityTimeout - timeSinceActivity) / 1000);
        
        if (!showModal) {
          setShowModal(true);
          setSecondsRemaining(remaining);

          if (warningIntervalRef.current) {
            clearInterval(warningIntervalRef.current);
          }
          warningIntervalRef.current = setInterval(() => {
            setSecondsRemaining((prev) => {
              if (prev <= 1) {
                if (logoutRef.current) {
                  logoutRef.current();
                }
                return 0;
              }
              return prev - 1;
            });
          }, 1000);
        } else {
          setSecondsRemaining(remaining);
        }
      } else if (timeSinceActivity >= inactivityTimeout) {
        if (logoutRef.current) {
          logoutRef.current();
        }
      }
    };

    intervalRef.current = setInterval(checkSession, checkInterval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (warningIntervalRef.current) {
        clearInterval(warningIntervalRef.current);
      }
    };
  }, [token, warningTime, checkInterval, inactivityTimeout, showModal]);

  const handleExtend = useCallback(async () => {
    if (isExtendingRef.current) return;
    
    isExtendingRef.current = true;
    try {
      await extendSession().unwrap();
      lastActivityRef.current = Date.now();
      setShowModal(false);
      setSecondsRemaining(warningTime);
      if (warningIntervalRef.current) {
        clearInterval(warningIntervalRef.current);
        warningIntervalRef.current = null;
      }
    } catch (error) {
      console.error('Failed to extend session:', error);
      if (logoutRef.current) {
        logoutRef.current();
      }
    } finally {
      isExtendingRef.current = false;
    }
  }, [extendSession, warningTime]);

  return {
    showModal,
    secondsRemaining,
    handleExtend,
    handleLogout,
  };
};

